import React from 'react';

import type { SiftConfig } from '../CGStep';
import type { InteractionAction } from '../HistoryStep';
import type { StagingCandidate } from '../Input2/CampaignButtonWithCadence';
import type { Document } from '../types';
import type {
  AIMessageSectionData,
  CadenceGraph,
  DocumentData,
  StepCreateInput,
  StepUpdateInput,
  TemplateInfo,
} from './CadenceSectionPure';
import type { RetryState } from './ContactDetailPageCore';
import type {
  Cadence,
  CadenceStep,
  ContactDetailUserTeam,
  ContactPrimaryFields,
  ContactUserInteraction,
  HistoryData,
  Org,
} from './types';
import { cn } from '../cn';
import { InteractionStep } from '../HistoryStep';
import { Button, ButtonGroup } from '../Input2';
import { Arrows, General, Layout } from '../StrokeIcons';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { WaveSpinner } from '../WaveSpinner';
import { CadenceMinimalPure } from './CadenceMinimalPure';
import { CadenceSectionPure } from './CadenceSectionPure';

// Type aliases to avoid importing from pg-gen
type ContactInteractionAction = 'message_received' | 'email_received' | 'inmail_received' | 'document_viewed' | string;
type CadenceStepType =
  | 'message'
  | 'connect'
  | 'inmail'
  | 'visit'
  | 'follow'
  | 'email'
  | 'reminder'
  | 'call'
  | 'delay'
  | 'likeRecentPosts'
  | 'wait_for_connect'
  | 'voice_note'
  | 'sift'
  | 'switch_campaign';

// AtVariables simplified type
type AtVariableDict = Record<string, string>;

// ============================================================================
// Types
// ============================================================================

export type CadenceData = {
  cadence: Cadence & {
    state?: string;
    nextStepTypes?: string[];
    nextRunTime?: Date | string | null;
    nextStepBody?: string | null;
    nextStepSubject?: string | null;
    // Server data uses `templateId`; the local Cadence type uses
    // `cadenceTemplateId`. Accept both so we can coalesce downstream.
    templateId?: string | null;
  };
  cadenceSteps: CadenceStep[] | null;
};

/**
 * Density of the campaign-steps area on the Interact tab. `minimal`
 * collapses the cadence to a compact row of step icons; `expanded` (default)
 * shows the full editable step cards. Mirrors the switch-graph toggle.
 */
export type InteractDisplayMode = 'minimal' | 'expanded';

export type ContactInteractSectionPureProps = {
  currentUserId: string;
  // Flattened from Contact - only fields actually used
  contactId: string;
  contactPrimaryFields?: ContactPrimaryFields;
  contactLiAbout?: string | null;
  /**
   * True when the auth user is already a LinkedIn connection of this contact.
   * Forwarded into the cadence card so the picker disables the Connect option
   * with an explanatory tooltip.
   */
  contactAlreadyConnectedOnLinkedIn?: boolean;
  cadenceData?: CadenceData | null;
  org?: Org;
  documents?: Document[];
  response?: ContactUserInteraction;
  atVariables: Record<string, string> & AtVariableDict;

  // Data that was previously fetched internally
  history: HistoryData;
  historyIsLoading: boolean;

  // Interaction callbacks
  onClickToRespond?: (type: ContactInteractionAction) => void;
  onClickCadence?: (cadenceId: string) => void;
  onMarkMeetingBooked: (interactionPublicId: string) => void;
  isMarkingMeetingBooked?: boolean;

  // Retry attachment
  onRetryAttachment?: (interactionId: string) => void;
  retryStates?: Record<string, RetryState>;
  retryErrors?: Record<string, string | null>;

  // Transcription request
  onRequestTranscription?: (interactionId: string) => void;
  transcriptionRequestStates?: Record<string, 'idle' | 'pending' | 'success' | 'error'>;

  // Cadence section data
  cadenceStepsGraph?: CadenceGraph | null;
  cadenceTemplateInfo?: TemplateInfo | null;
  cadenceDocuments?: DocumentData[];

  // Cadence section callbacks
  onCadenceCreateStep?: (data: StepCreateInput) => unknown;
  onCadenceUpdateStep?: (stepId: string, data: StepUpdateInput) => unknown;
  onCadenceDeleteStep?: (stepId: string) => unknown;
  onCadenceChangeStepType?: (stepId: string, type: string) => unknown;
  onCadenceMarkStepFinished?: (stepId: string) => unknown;
  onToggleInmailOpen?: (nodeId: string, onlyOpenLink: boolean) => unknown;

  // Smart-campaign AI Message Section removed; keep optional so callers
  // can still pass `null` until callsites are cleaned up.
  aiMessageData?: AIMessageSectionData | null;

  // Render props for platform-specific components
  renderSiftConditionBuilder?: (props: { value: SiftConfig; onChange: (value: SiftConfig) => void }) => React.ReactNode;

  // Reminder editor — list of available reminder actions for the team and a
  // create-on-the-fly callback (passes through to CadenceSectionPure).
  reminderActions?: { id: string; name: string }[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;

  /**
   * Non-terminal candidate rows for this contact. When there's no active
   * cadence yet (`cadenceData == null`), this drives a "candidate in
   * progress" card in place of the empty cadence area — so reps see
   * "Generating dossier for X" instead of an empty Interact tab while
   * the candidate-research worker chews through Unipile + dossier.
   */
  stagingCandidates?: StagingCandidate[];
  /** Click target for the candidate card — same handler as the staging badge. */
  onStagingClick?: (candidate: StagingCandidate) => void;
  /**
   * Force-retry callback for the candidate card. Resets the candidate
   * back to `pending` and re-enqueues the worker chain — same call the
   * staging UI's Retry button makes. Surfaced on every in-progress and
   * failed candidate so reps can unstick rows the cron hasn't caught
   * yet (10-min watchdog window). Hidden for `pending_review` because
   * those need approval, not retry.
   */
  onStagingRetry?: (candidate: StagingCandidate) => Promise<unknown>;
  /** Candidate id currently mid-retry, for spinner / disabled state. */
  stagingRetryingCandidateId?: number | null;
  voiceEnabled?: boolean;
  voiceCallingEnabled?: boolean;
  /**
   * Injected dialer for an action-required `call` step in the cadence timeline. Forwarded
   * to `CadenceSectionPure` → the timeline row so the rep can place the call in place.
   */
  renderCadenceCallButton?: (args: { cadenceStepId: string }) => React.ReactNode;
  /** Completes a `call` step handled outside the dialer — see `CallStepActionsPure`. */
  onMarkCallStepDone?: (args: {
    cadenceStepId: string;
    outcome: string;
    notes?: string;
    callbackAt?: number;
  }) => Promise<void> | void;
  /** Origin for the "See in template" link — see CadenceSectionPureProps. */
  templateLinkBaseUrl?: string;
  /**
   * Touch-campaign graph renderer. When provided (contact enrolled in a touch
   * campaign), the interact tab renders this editable node-canvas in place of
   * the vertical `CadenceSectionPure` step timeline. Injected as a render prop
   * because the graph lives in `apps/nextjs` and can't be imported into this
   * shared package — same layering reason as `renderSiftConditionBuilder`.
   */
  renderTouchCampaignGraph?: () => React.ReactNode;

  /**
   * Campaign-steps density. Defaults to `expanded`. When
   * `onInteractDisplayChange` is also provided AND there's a steps graph to
   * collapse, a Minimal/Expanded toggle renders above the cadence.
   */
  interactDisplay?: InteractDisplayMode;
  /**
   * Persists the density choice (e.g. to a URL query param). Omit on hosts
   * that don't support the toggle — the toggle is hidden and the section
   * always renders expanded (no dead control).
   */
  onInteractDisplayChange?: (mode: InteractDisplayMode) => void;
};

// Legacy type for backwards compatibility - deprecated
export type CadenceSectionRenderProps = {
  cadenceData: NonNullable<CadenceData>;
  contactId: string;
  contactPrimaryFields?: ContactPrimaryFields;
  contactLiAbout?: string | null;
  userTeam: ContactDetailUserTeam;
  atVariables: Record<string, string> & AtVariableDict;
  org?: Org;
};

// ============================================================================
// Helper Functions
// ============================================================================

function formatStepType(type: CadenceStepType): string {
  const typeMap: Record<CadenceStepType, string> = {
    message: 'Message',
    connect: 'Connect',
    inmail: 'InMail',
    visit: 'Visit Profile',
    follow: 'Follow',
    email: 'Email',
    reminder: 'Reminder',
    call: 'Call',
    delay: 'Delay',
    likeRecentPosts: 'Like Posts',
    wait_for_connect: 'Wait for Connect',
    voice_note: 'Voice Note',
    sift: 'Filter',
    switch_campaign: 'Switch Campaign',
  };
  return typeMap[type] || type;
}

// ============================================================================
// Loading State
// ============================================================================

export function LoadingSection() {
  return (
    <div className="flex flex-col gap-3 p-4">
      <LoadingCard />
      <LoadingCard />
      <LoadingCard />
    </div>
  );
}

function LoadingCard() {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-astronaut-100 bg-white p-3 dark:border-astronaut-800 dark:bg-astronaut-900">
      <WaveSpinner className="size-5" />
      <div className="h-4 w-32 animate-pulse rounded-sm bg-astronaut-100 dark:bg-astronaut-800" />
    </div>
  );
}

// ============================================================================
// Candidate-in-progress card
// ============================================================================

/**
 * Maps a candidate's status to a single-sentence "what's happening right
 * now" string. Mirrors the badge tooltip copy on
 * `CampaignButtonWithCadence`, but written in active voice so it reads
 * cleanly in a card body.
 */
function candidateStatusLine(status: StagingCandidate['status'], templateName: string): string {
  switch (status) {
    case 'pending':
      return `Queued for research before enrolling in ${templateName}.`;
    case 'researching':
      return `Pulling LinkedIn profile data for ${templateName}.`;
    case 'research_failed':
      return `Research failed before enrolling in ${templateName}.`;
    case 'gathering_sources':
      return `Searching the web for context before enrolling in ${templateName}.`;
    case 'synthesizing':
      return `Synthesizing research dossier for ${templateName} — usually 30 seconds.`;
    case 'synthesize_failed':
      return `Dossier generation failed for ${templateName}.`;
    case 'filter_failed':
      return `Filtered out of ${templateName} — campaign rules didn't match.`;
    case 'personalizing':
      return `AI is drafting personalized messages for ${templateName}.`;
    case 'personalize_failed':
      return `Message drafting failed for ${templateName}.`;
    case 'rate_limited':
      return `Paused — LinkedIn is rate limiting this account. ${templateName} resumes automatically.`;
    case 'account_disconnected':
      return `Paused — reconnect LinkedIn to resume ${templateName}.`;
    case 'awaiting_capacity':
      return `Waiting for capacity before continuing ${templateName}.`;
    case 'retrying':
      return `Retrying after a transient error before enrolling in ${templateName}.`;
    case 'pending_review':
      return `AI drafts ready — review them in ${templateName}'s staging to enroll.`;
    case 'rejected':
      return `Enrollment in ${templateName} was rejected.`;
  }
}

const IN_PROGRESS_STATUSES: ReadonlySet<StagingCandidate['status']> = new Set([
  'pending',
  'researching',
  'gathering_sources',
  'synthesizing',
  'personalizing',
  // Blocked statuses are alive and auto-resume — treat as in-progress, not failure.
  'rate_limited',
  'account_disconnected',
  'awaiting_capacity',
  'retrying',
]);
const FAILURE_STATUSES: ReadonlySet<StagingCandidate['status']> = new Set([
  'research_failed',
  'synthesize_failed',
  'filter_failed',
  'personalize_failed',
  'rejected',
]);

/**
 * Renders one card per staging candidate so the rep sees what's happening
 * before promotion. The "in progress" statuses get a spinner + blue tint;
 * failures get a red tint; `pending_review` gets a yellow CTA. Clicking
 * the card hands off to the same staging route the header badge uses.
 */
function CandidateInProgressCard({
  candidates,
  onClick,
  onRetry,
  retryingCandidateId,
}: {
  candidates: StagingCandidate[];
  onClick?: (candidate: StagingCandidate) => void;
  onRetry?: (candidate: StagingCandidate) => Promise<unknown>;
  retryingCandidateId?: number | null;
}) {
  return (
    <div className="bg-white p-3 dark:bg-stone-900">
      <div className="flex flex-col gap-2">
        {candidates.map((c) => {
          const isInProgress = IN_PROGRESS_STATUSES.has(c.status);
          const isFailure = FAILURE_STATUSES.has(c.status);
          const isReview = c.status === 'pending_review';
          // Retry button shows on every state EXCEPT pending_review (those
          // need approval, not retry) — covers both watchdog-style force
          // retries during a stuck dossier and the standard retry on a
          // hard failure row.
          const showRetry = onRetry != null && !isReview;
          const isRetrying = retryingCandidateId === c.candidateId;
          const tintClasses = isInProgress
            ? 'border-blue-200 bg-blue-50 dark:border-blue-900/60 dark:bg-blue-950/40'
            : isReview
              ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-900/60 dark:bg-yellow-950/40'
              : isFailure
                ? 'border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/40'
                : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900';
          const textClasses = isFailure
            ? 'text-red-900 dark:text-red-200'
            : isReview
              ? 'text-yellow-900 dark:text-yellow-200'
              : 'text-gray-900 dark:text-gray-100';

          // The card is a div + onClick (not a <button>) so the inline
          // Retry button isn't nested inside another button — that's
          // invalid HTML and breaks accessibility (CLAUDE.md rule).
          return (
            <div
              key={c.candidateId}
              role={onClick ? 'button' : undefined}
              tabIndex={onClick ? 0 : undefined}
              onClick={() => onClick?.(c)}
              onKeyDown={(e) => {
                if (onClick && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onClick(c);
                }
              }}
              className={cn(
                'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                tintClasses,
                onClick && 'cursor-pointer hover:brightness-95 dark:hover:brightness-110',
              )}>
              {isInProgress && !isRetrying && <WaveSpinner className="mt-0.5 size-5 shrink-0" />}
              <div className={cn('flex min-w-0 flex-1 flex-col gap-1', textClasses)}>
                <div className="text-sm font-semibold">{c.templateName}</div>
                <div className="text-sm">{candidateStatusLine(c.status, c.templateName)}</div>
                {c.failureReason && isFailure && <div className="text-xs opacity-80">{c.failureReason}</div>}
              </div>
              {showRetry && (
                <Button
                  size="xs"
                  color={isFailure ? 'red' : 'blue'}
                  tone={isFailure ? 'filled' : 'soft'}
                  icon={<Arrows.RefreshCcw05 />}
                  disabled={isRetrying}
                  onClick={(e) => {
                    e.stopPropagation();
                    void onRetry?.(c);
                  }}
                  tooltip="Reset this candidate back to pending and re-run the worker (use this to unstick a candidate the cron hasn't recovered yet)">
                  {isRetrying ? 'Retrying…' : 'Force retry'}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * NoCadenceStepInfo — explains why the "Add a step" / "Add step on a new
 * day" affordance is absent for this contact.
 *
 * The add-step affordance lives inside CadenceSectionPure, which only
 * renders when the contact is enrolled in a campaign (active cadence). For
 * contacts without one, the option silently disappeared — reps read that as
 * an inconsistent/broken UI (POS-2325). This card makes the conditional
 * explicit: steps are scheduled as part of a campaign, so without one
 * there's nothing to add a step to.
 */
function NoCadenceStepInfo() {
  return (
    <div className="bg-white p-3 dark:bg-stone-900">
      <div
        data-testid="no-cadence-step-info"
        className={cn(
          'flex items-start gap-3 rounded-lg border p-3 text-left',
          'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900',
        )}>
        <General.InfoCircle className="mt-0.5 size-5 shrink-0 text-gray-400 dark:text-gray-500" />
        <div className="flex min-w-0 flex-1 flex-col gap-1 text-gray-900 dark:text-gray-100">
          <div className="text-sm font-semibold">No active campaign</div>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Steps are scheduled as part of a campaign. Enroll this contact in a campaign to add a step on a new day.
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Next Step Preview
// ============================================================================

type NextStepPreviewProps = {
  cadenceData: NonNullable<CadenceData>;
};

function NextStepPreview({ cadenceData }: NextStepPreviewProps) {
  if (!cadenceData.cadence.nextStepTypes || cadenceData.cadence.nextStepTypes.length === 0) {
    return null;
  }

  return (
    <div className="p-3">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
        <div className="flex items-start gap-2">
          <div className="mt-0.5 shrink-0">
            <svg
              className="size-4 text-blue-600 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <p className="mb-1.5 truncate text-xs font-semibold text-blue-900 dark:text-blue-100">
              Next Step: {cadenceData.cadence.name}
            </p>
            <div className="mb-1.5 flex flex-wrap gap-1.5">
              {cadenceData.cadence.nextStepTypes.map((stepType: string) => (
                <span
                  key={stepType}
                  data-testid="next-step-type-badge"
                  className="inline-flex items-center rounded-sm border border-blue-300 bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:border-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                  {formatStepType(stepType as CadenceStepType)}
                </span>
              ))}
            </div>
            {cadenceData.cadence.nextRunTime && (
              <p className="mb-2 text-xs text-blue-600 dark:text-blue-400">
                Scheduled for: {new Date(cadenceData.cadence.nextRunTime).toLocaleString()}
              </p>
            )}
            {cadenceData.cadence.nextStepBody && (
              <div className="mt-2 border-t border-blue-200 pt-2 dark:border-blue-800">
                {cadenceData.cadence.nextStepSubject && (
                  <p className="mb-1 text-xs font-semibold text-blue-900 dark:text-blue-100">
                    Subject: {cadenceData.cadence.nextStepSubject}
                  </p>
                )}
                <div
                  className="whitespace-pre-wrap text-xs text-blue-800 dark:text-blue-200"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(cadenceData.cadence.nextStepBody) }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ContactInteractSectionPure - Main Component
// ============================================================================

export function ContactInteractSectionPure({
  currentUserId,
  contactId,
  contactPrimaryFields,
  contactLiAbout,
  contactAlreadyConnectedOnLinkedIn,
  cadenceData,
  org,
  response,
  atVariables,
  history,
  historyIsLoading,
  onClickToRespond,
  onClickCadence,
  onMarkMeetingBooked,
  isMarkingMeetingBooked,
  onRetryAttachment,
  retryStates,
  retryErrors,
  onRequestTranscription,
  transcriptionRequestStates,
  cadenceStepsGraph,
  cadenceTemplateInfo,
  cadenceDocuments,
  onCadenceCreateStep,
  onCadenceUpdateStep,
  onCadenceDeleteStep,
  onCadenceChangeStepType,
  onCadenceMarkStepFinished,
  onToggleInmailOpen,
  aiMessageData: _aiMessageData,
  renderSiftConditionBuilder,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  stagingCandidates,
  onStagingClick,
  onStagingRetry,
  stagingRetryingCandidateId,
  voiceEnabled,
  voiceCallingEnabled,
  renderCadenceCallButton,
  onMarkCallStepDone,
  templateLinkBaseUrl,
  renderTouchCampaignGraph,
  interactDisplay = 'expanded',
  onInteractDisplayChange,
}: ContactInteractSectionPureProps) {
  const isLoading = historyIsLoading;
  const historyLength = history?.length ?? 0;
  const doesCadenceExist = cadenceData != null;

  // The Minimal/Expanded toggle only makes sense when there's a steps graph
  // to collapse and a host that persists the choice. Gate `minimal` on the
  // same condition so a persisted `minimal` can never strand the user on an
  // empty, un-toggleable view.
  const canToggleDisplay = onInteractDisplayChange != null && cadenceStepsGraph != null;
  const isMinimal = canToggleDisplay && interactDisplay === 'minimal';

  // Scroll effects
  React.useEffect(() => {
    if (response != null) {
      document.querySelector('div#cadence_event')?.scrollIntoView();
    } else if (doesCadenceExist) {
      // Scroll to cadence section
      const divider = document.getElementById('scheduled_divider');
      if (divider) {
        divider.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      document.querySelector('div#cadence_section')?.scrollIntoView({ block: 'end' });
    }
  }, [historyLength, response, doesCadenceExist]);

  return (
    <div className={cn('relative h-0 w-full grow overflow-hidden')}>
      <div
        className={cn(
          'h-full overflow-y-auto bg-stone-50 transition-all duration-200 ease-out dark:bg-stone-800',
          cadenceData?.cadence?.state === 'paused' ? 'dark:bg-stone-800' : '',
        )}>
        {historyIsLoading && <LoadingSection />}

        <div className="dark:bg-stone-800" id="cadence_section">
          {!isLoading && (
            <>
              {/* History Section */}
              {historyLength > 0 && (
                <>
                  <div className="bg-white p-3 dark:bg-stone-900">
                    <div className="flex flex-col">
                      {history?.map((interactionObj: HistoryData[number], i: number) => {
                        const interaction = interactionObj.interaction;
                        const interactionId = interaction.odooInteractionId ?? String(interaction.id);
                        return (
                          <InteractionStep
                            key={interactionId}
                            currentUserId={currentUserId}
                            contactFirstName={interactionObj.contact?.primaryFields?.firstName?.data}
                            contactLastName={interactionObj.contact?.primaryFields?.lastName?.data}
                            interactionId={interactionId}
                            interactionUserId={interaction.userId}
                            interactionAction={
                              (interaction.odooInteractionAction ?? interaction.interactionType) as InteractionAction
                            }
                            interactionDate={interaction.createdAt}
                            interactionUpdatedOn={interaction.updatedOn ?? interaction.createdAt}
                            interactionSubject={interaction.subject}
                            interactionBody={interaction.body}
                            interactionFromNumber={interaction.from}
                            interactionToNumber={interaction.to}
                            interactionDataUrl={interaction.dataUrl}
                            interactionDataUrlType={interaction.dataUrlType}
                            actorName={interactionObj.interactionUser?.name}
                            actorEmail={interactionObj.interactionUser?.email ?? ''}
                            documentId={interactionObj.documentView?.id}
                            documentTitle={interactionObj.documentView?.title}
                            documentOgImageUrl={interactionObj.documentView?.ogImageUrl}
                            cadenceName={interactionObj.cadence?.name}
                            cadenceId={interactionObj.cadence?.id}
                            onClickCadence={onClickCadence}
                            prevInteractionDate={history[i - 1]?.interaction?.createdAt}
                            isLast={i === history.length - 1}
                            onClickToRespond={onClickToRespond}
                            onMarkMeetingBooked={onMarkMeetingBooked}
                            isMarkingMeetingBooked={isMarkingMeetingBooked}
                            onRetryAttachment={onRetryAttachment}
                            retryState={retryStates?.[interactionId] ?? 'idle'}
                            retryError={retryErrors?.[interactionId]}
                            transcription={interaction.transcription}
                            transcriptionModel={interaction.transcription_model}
                            transcriptionStatus={interaction.transcription_status}
                            transcriptionError={interaction.transcription_error}
                            onRequestTranscription={onRequestTranscription}
                            transcriptionRequestState={transcriptionRequestStates?.[interactionId] ?? 'idle'}
                            meetingBookedFromThis={interaction.meetingBookedFromThis}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <div
                    className={cn(
                      '-mb-2.5 -mt-3 h-10 bg-linear-to-t from-stone-50 to-white duration-200',
                      'dark:bg-linear-to-t dark:from-stone-800 dark:to-stone-900',
                    )}
                  />
                </>
              )}

              {/* Candidate-in-progress card — when there's no active
                  cadence yet, surface the candidate-pipeline status (e.g.
                  "Generating dossier for X") so the rep doesn't see an
                  empty Interact tab while the worker chews through Unipile
                  / dossier / personalization. Hidden once a cadence
                  exists; the Next Step Preview takes over. */}
              {!cadenceData && (stagingCandidates ?? []).length > 0 && (
                <CandidateInProgressCard
                  candidates={stagingCandidates!}
                  onClick={onStagingClick}
                  onRetry={onStagingRetry}
                  retryingCandidateId={stagingRetryingCandidateId}
                />
              )}

              {/* No-campaign explanation — when there's no active cadence
                  and no candidate mid-research, the add-step affordance
                  (which lives inside CadenceSectionPure) can't render. Tell
                  the rep why instead of leaving an empty area that reads as
                  an inconsistent/broken UI (POS-2325). */}
              {!cadenceData && (stagingCandidates ?? []).length === 0 && <NoCadenceStepInfo />}

              {/* Next Step Preview */}
              {cadenceData && <NextStepPreview cadenceData={cadenceData} />}

              {/* Touch-campaign graph — replaces the vertical timeline when
                  the contact is enrolled in a touch campaign. It's the same
                  editable node-canvas the template page shows (edits persist
                  to the shared template). Injected as a render prop from the
                  host because it lives in apps/nextjs. */}
              {cadenceData && renderTouchCampaignGraph != null && (
                <div className="bg-white p-3 dark:bg-stone-900">{renderTouchCampaignGraph()}</div>
              )}

              {/* Cadence Section (linear timeline). Renders whenever there
                  are steps to show — CadenceSectionPure derives read-only
                  mode itself when any step-mutation callback is missing (e.g.
                  the extension sidepanel, which displays the plan but can't
                  edit it yet). Without callbacks AND without a steps graph
                  there's nothing to render, so skip the wrapper. Skipped for
                  touch campaigns, which render the node-graph above. */}
              {cadenceData &&
                renderTouchCampaignGraph == null &&
                (cadenceStepsGraph != null ||
                  (onCadenceCreateStep &&
                    onCadenceUpdateStep &&
                    onCadenceDeleteStep &&
                    onCadenceChangeStepType &&
                    onCadenceMarkStepFinished &&
                    onToggleInmailOpen)) && (
                  <div className="bg-white p-3 dark:bg-stone-900">
                    {/* Minimal ↔ Expanded density toggle — mirrors the switch
                      graph. Only shown when the host persists the choice and
                      there's a steps graph to collapse. */}
                    {canToggleDisplay && (
                      <div className="mb-2 flex justify-end" data-testid="interact-display-toggle">
                        <ButtonGroup size="xs">
                          <Button
                            data-control="interact-minimal"
                            color={isMinimal ? 'blue' : 'gray'}
                            tone={isMinimal ? 'filled' : 'outline'}
                            icon={<Layout.Rows03 />}
                            tooltip="Minimal — compact step icons"
                            onClick={() => onInteractDisplayChange?.('minimal')}>
                            Minimal
                          </Button>
                          <Button
                            data-control="interact-expanded"
                            color={isMinimal ? 'gray' : 'blue'}
                            tone={isMinimal ? 'outline' : 'filled'}
                            icon={<Layout.LayoutGrid02 />}
                            tooltip="Expanded — full step editor"
                            onClick={() => onInteractDisplayChange?.('expanded')}>
                            Expanded
                          </Button>
                        </ButtonGroup>
                      </div>
                    )}

                    {isMinimal && <CadenceMinimalPure stepsGraph={cadenceStepsGraph ?? null} />}

                    {!isMinimal && (
                      <CadenceSectionPure
                        cadence={{
                          id: cadenceData.cadence.id,
                          // Server data populates `templateId` directly; the local
                          // Cadence type uses `cadenceTemplateId`. Coalesce so the
                          // "Not in template" link renders for cadences spawned
                          // from a template, regardless of which field the host
                          // happens to surface.
                          templateId: cadenceData.cadence.templateId || cadenceData.cadence.cadenceTemplateId || null,
                          createdOn: cadenceData.cadence.createdAt,
                        }}
                        stepsGraph={cadenceStepsGraph ?? null}
                        templateInfo={cadenceTemplateInfo}
                        documents={cadenceDocuments}
                        contactInfo={{
                          id: contactId,
                          name:
                            [contactPrimaryFields?.firstName?.data, contactPrimaryFields?.lastName?.data]
                              .filter(Boolean)
                              .join(' ') || undefined,
                          company: org?.name || contactPrimaryFields?.company?.data || undefined,
                          title: contactPrimaryFields?.title?.data || undefined,
                          headline: contactPrimaryFields?.headline?.data || undefined,
                          about: contactLiAbout || undefined,
                          city: contactPrimaryFields?.city?.data || undefined,
                          alreadyConnectedOnLinkedIn: contactAlreadyConnectedOnLinkedIn,
                        }}
                        atVariables={atVariables}
                        onCreateStep={onCadenceCreateStep}
                        onUpdateStep={onCadenceUpdateStep}
                        onDeleteStep={onCadenceDeleteStep}
                        onChangeStepType={onCadenceChangeStepType}
                        onMarkStepFinished={onCadenceMarkStepFinished}
                        onToggleInmailOpen={onToggleInmailOpen}
                        renderSiftConditionBuilder={renderSiftConditionBuilder}
                        reminderActions={reminderActions}
                        reminderActionsLoading={reminderActionsLoading}
                        onCreateReminder={onCreateReminder}
                        reminderCreating={reminderCreating}
                        onReminderError={onReminderError}
                        voiceEnabled={voiceEnabled}
                        voiceCallingEnabled={voiceCallingEnabled}
                        renderCadenceCallButton={renderCadenceCallButton}
                        onMarkCallStepDone={onMarkCallStepDone}
                        templateLinkBaseUrl={templateLinkBaseUrl}
                      />
                    )}
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
